import { useState, useEffect, useCallback } from "react";

// formats da çekiliyor: tam ekran kapak görseli için cihaza uygun (daha küçük) varyant seçilebilsin.
const API_URL =
  "/api/web-home-photo" +
  "?populate[fotograflar][fields][0]=url&populate[fotograflar][fields][1]=formats" +
  "&populate[ilk_fotograf][fields][0]=url&populate[ilk_fotograf][fields][1]=formats";
const BASE_URL = "";
const CACHE_KEY = "web_home_photo_cache_v2"; // v2: formats/displayUrl eklendi (eski cache geçersiz)
const CACHE_EXPIRY = 30 * 60 * 1000; // 30 dakika

function normalizeUrl(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${BASE_URL}${url}`;
}

// Tam ekran (object-fit: cover) + %45 koyu overlay arkasındaki kapak görseli için
// cihaza uygun varyantı seç. Mobil/tablet/orta masaüstünde 'large' (~1000px, ~94KB)
// tam yeterli; sadece geniş ekranlarda tam çözünürlüğü kullan. (260KB -> ~94KB mobil LCP)
function pickDisplayUrl(media) {
  if (!media) return null;
  const f = media.formats || {};
  const w = (typeof window !== "undefined" && window.innerWidth) || 1280;
  const variant = w <= 1440 ? (f.large || f.medium || f.small) : null;
  return normalizeUrl((variant && variant.url) || media.url);
}

function mapImage(media) {
  if (!media) return null;
  return { ...media, url: normalizeUrl(media.url), displayUrl: pickDisplayUrl(media) };
}

export default function useWebHomePhoto() {
  const [ilkFoto, setIlkFoto] = useState(null);
  const [fotograflar, setFotograflar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAndCache = useCallback(async () => {
    setLoading(true);
    setError(null);
    let cacheUsed = false;
    try {
      const res = await fetch(API_URL);
      if (!res.ok) throw new Error("Görseller alınamadı");
      const json = await res.json();
      const data = json.data || {};

      // ilk_fotograf
      const ilk = mapImage(data.ilk_fotograf);

      // fotograflar
      const fotos = Array.isArray(data.fotograflar)
        ? data.fotograflar.map(mapImage)
        : [];

      // Cache
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          ilkFoto: ilk,
          fotograflar: fotos,
          timestamp: Date.now()
        })
      );

      setIlkFoto(ilk);
      setFotograflar(fotos);
    } catch (err) {
      // Hata durumunda cache'den oku
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { ilkFoto, fotograflar } = JSON.parse(cached);
          if (ilkFoto && fotograflar && fotograflar.length > 0) {
            setIlkFoto(ilkFoto);
            setFotograflar(fotograflar);
            cacheUsed = true;
          }
        }
      } catch {
        // intentionally ignored cache read error
      }
      setError(err.message || "Bir hata oluştu");
      if (!cacheUsed) {
        setIlkFoto(null);
        setFotograflar([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Önce cache kontrolü
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { ilkFoto, fotograflar, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_EXPIRY) {
          setIlkFoto(ilkFoto);
          setFotograflar(fotograflar);
          setLoading(false);
          return;
        }
      }
    } catch {
      // Ignore cache read errors
    }
    fetchAndCache();
  }, [fetchAndCache]);

  // Yeniden yükleme fonksiyonu
  const refetch = fetchAndCache;

  return {
    ilkFoto,
    fotograflar,
    loading,
    error,
    refetch
  };
}
